import { LOCAL_DB_NAME, LOCAL_DB_VERSION } from "../constants";
import type { QueueItem, RitualSnapshot } from "../types";

const SNAPSHOT_KEY = "ritual";
const SNAPSHOT_STORE = "snapshot";
const QUEUE_STORE = "queue";

let dbPromise: Promise<IDBDatabase> | null = null;

function openLocalDb() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(LOCAL_DB_NAME, LOCAL_DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) {
        db.createObjectStore(SNAPSHOT_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        db.createObjectStore(QUEUE_STORE, { keyPath: "id" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

function requestToPromise<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function store<T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (objectStore: IDBObjectStore) => IDBRequest<T>,
) {
  const db = await openLocalDb();
  const tx = db.transaction(storeName, mode);
  const result = await requestToPromise(work(tx.objectStore(storeName)));
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
  return result;
}

export async function getLocalSnapshot(): Promise<RitualSnapshot | null> {
  const record = await store<{ id: string; snapshot: RitualSnapshot } | undefined>(
    SNAPSHOT_STORE,
    "readonly",
    (objectStore) => objectStore.get(SNAPSHOT_KEY),
  );

  return record?.snapshot ?? null;
}

export async function putLocalSnapshot(snapshot: RitualSnapshot) {
  await store(SNAPSHOT_STORE, "readwrite", (objectStore) =>
    objectStore.put({ id: SNAPSHOT_KEY, snapshot }),
  );
}

export async function getQueue(): Promise<QueueItem[]> {
  return store<QueueItem[]>(QUEUE_STORE, "readonly", (objectStore) => objectStore.getAll());
}

export async function putQueueItem(item: QueueItem) {
  await store(QUEUE_STORE, "readwrite", (objectStore) => objectStore.put(item));
}

export async function deleteQueueItem(id: string) {
  await store(QUEUE_STORE, "readwrite", (objectStore) => objectStore.delete(id));
}

export async function clearQueue() {
  const db = await openLocalDb();
  const tx = db.transaction(QUEUE_STORE, "readwrite");
  tx.objectStore(QUEUE_STORE).clear();
  await new Promise<void>((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}
